import { useState, useRef, useEffect } from 'react';
import api, { getRegistros, getHistoricoImportacoes } from '../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, LabelList } from 'recharts';
function hojeISO() { return new Date().toISOString().slice(0, 10); }
function decParaHHMM(val) {
  if (val == null || val === '') return '—';
  const totalMin = Math.round(parseFloat(val) * 60);
  const h = Math.floor(Math.abs(totalMin) / 60);
  const m = Math.abs(totalMin) % 60;
  return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`;
}
function isoParaBR(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}
function nomeArquivoParaISO(nome) {
  const match = nome.match(/(\d{2})[.\-_/](\d{2})[.\-_/](\d{4})/);
  if (match) return `${match[3]}-${match[2]}-${match[1]}`;
  return null;
}

export default function ImportarRegistros() {
  const [arquivo, setArquivo] = useState(null);
  const [data, setData]       = useState(hojeISO());
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [erro, setErro]       = useState('');
  const [historico, setHistorico] = useState([]);
  const [confirmModal, setConfirmModal] = useState(null); // { dataFormatada, em }
  const [detalheModal, setDetalheModal] = useState(null); // { data, loading, registros, erro }
  const [setorFiltro, setSetorFiltro]   = useState('');
  const [pagHist, setPagHist] = useState(1);
  const [calMes, setCalMes] = useState(() => {
    const h = new Date(); return { ano: h.getFullYear(), mes: h.getMonth() };
  });
  const inputRef = useRef();
  const POR_PAG = 7;
  const MESES_CAL = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

  const abrirDetalhe = async (dataBR) => {
    setSetorFiltro('');
    setDetalheModal({ data: dataBR, loading: true, registros: [], erro: '' });
    try {
      const r = await getRegistros({ data_inicio: dataBR, data_fim: dataBR });
      setDetalheModal({ data: dataBR, loading: false, registros: r.data.results || r.data, erro: '' });
    } catch {
      setDetalheModal({ data: dataBR, loading: false, registros: [], erro: 'Erro ao carregar registros.' });
    }
  };

  const carregarHistorico = async () => {
    try {
      const res = await getHistoricoImportacoes();
      setHistorico(res.data.map(h => ({
        ...h,
        data: h.data_registro,
      })));
    } catch { /* ignora */ }
  };

  useEffect(() => { carregarHistorico(); }, []); // eslint-disable-line

  const handleFile = (file) => {
    if (!file) return;
    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      setErro('Formato inválido. Use arquivos .xlsx ou .xls');
      return;
    }
    const iso = nomeArquivoParaISO(file.name);
    if (iso) setData(iso);
    setArquivo(file);
    setErro('');
    setResultado(null);
  };

  const executarImportacao = async () => {
    setLoading(true); setErro(''); setResultado(null);
    const fd = new FormData();
    fd.append('arquivo', arquivo);
    fd.append('data', isoParaBR(data));
    try {
      const res = await api.post('/importar-registros/', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResultado(res.data);
      await carregarHistorico();
      setArquivo(null);
    } catch (e) {
      setErro(e.response?.data?.erro || 'Erro ao importar arquivo');
    } finally {
      setLoading(false);
    }
  };

  const importar = () => {
    if (!arquivo) { setErro('Selecione um arquivo!'); return; }
    if (!data)    { setErro('Informe a data dos registros.'); return; }
    const dataFormatada = isoParaBR(data);
    const jaImportado = historico.find(h => h.data === dataFormatada);
    if (jaImportado) {
      setConfirmModal({ dataFormatada, em: jaImportado.em });
    } else {
      executarImportacao();
    }
  };

  // Dados do gráfico: importações por dia do mês do calendário
  const dadosGrafico = (() => {
    const mesStr = String(calMes.mes + 1).padStart(2, '0');
    const anoStr = String(calMes.ano);
    // Filtra apenas registros do mês exibido no calendário
    const doMes = historico.filter(h => {
      const parts = h.data.split('/');
      return parts[1] === mesStr && parts[2] === anoStr;
    });
    // Agrupa por dia — mantém import mais recente por dia (historico já vem do mais novo)
    const mapa = {};
    doMes.forEach(h => {
      const dia = h.data.split('/')[0];
      if (!mapa[dia]) {
        mapa[dia] = { com: h.com_registro ?? 0, sem: h.sem_registro ?? 0 };
      }
    });
    return Object.entries(mapa)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([dia, vals]) => ({ dia, com: vals.com, sem: vals.sem }));
  })();

  // Soma do mês do calendário (deduplica por dia — usa import mais recente por dia)
  const resumoMes = (() => {
    const mesStr = String(calMes.mes + 1).padStart(2, '0');
    const anoStr = String(calMes.ano);
    const doMes = historico.filter(h => {
      const p = h.data.split('/');
      return p[1] === mesStr && p[2] === anoStr;
    });
    const mapa = {};
    doMes.forEach(h => { const d = h.data.split('/')[0]; if (!mapa[d]) mapa[d] = h; });
    const unicos = Object.values(mapa);
    if (unicos.length === 0) return null;
    return {
      total:      unicos.reduce((a, h) => a + (h.total || 0), 0),
      importados: unicos.reduce((a, h) => a + (h.importados || 0), 0),
      erros:      unicos.reduce((a, h) => a + (h.erros || 0), 0),
      dias:       unicos.length,
    };
  })();

  // Mostra resultado atual ou resumo do mês
  const exibir = resultado || (resumoMes ? {
    data: `${MESES_CAL[calMes.mes]} ${calMes.ano}`,
    total:      resumoMes.total,
    importados: resumoMes.importados,
    erros:      resumoMes.erros,
    mensagem:   `${resumoMes.dias} dia(s) importado(s) em ${MESES_CAL[calMes.mes]}/${calMes.ano}: ${resumoMes.importados} de ${resumoMes.total} registros.`,
    log_erros: [],
    _doHistorico: true,
  } : null);
  const ok = exibir && exibir.erros === 0;

  // Calendário de importações
  const diasImportados = new Set(
    historico.map(h => {
      const [d, m, y] = h.data.split('/');
      return `${y}-${m}-${d}`;
    })
  );
  // Mapa de com_registro por dia
  const comRegistroMap = {};
  historico.forEach(h => {
    const [d, m, y] = h.data.split('/');
    const key = `${y}-${m}-${d}`;
    if (comRegistroMap[key] == null || h.com_registro > comRegistroMap[key])
      comRegistroMap[key] = h.com_registro ?? 0;
  });
  const maxComRegistro = Math.max(1, ...Object.values(comRegistroMap));
  function calDayStyle(isoKey, importado, ehHoje) {
    if (!importado) return {
      background: ehHoje ? '#dbeafe' : 'transparent',
      color: ehHoje ? '#2563eb' : '#64748b',
      border: ehHoje ? '1px solid #93c5fd' : '1px solid transparent',
    };
    const ratio = (comRegistroMap[isoKey] ?? 0) / maxComRegistro;
    let bg, color, border;
    if (ratio >= 0.85)      { bg = '#065f46'; color = '#fff';    border = '#064e3b'; }
    else if (ratio >= 0.6)  { bg = '#059669'; color = '#fff';    border = '#047857'; }
    else if (ratio >= 0.35) { bg = '#34d399'; color = '#064e3b'; border = '#6ee7b7'; }
    else                    { bg = '#d1fae5'; color = '#059669'; border = '#a7f3d0'; }
    return { background: bg, color, border: `1px solid ${border}` };
  }
  function buildCalendario(ano, mes) {
    const primeiroDia = new Date(ano, mes, 1).getDay();
    const totalDias = new Date(ano, mes + 1, 0).getDate();
    return { primeiroDia, totalDias };
  }
  const { primeiroDia, totalDias } = buildCalendario(calMes.ano, calMes.mes);
  const hoje = new Date().toISOString().slice(0, 10);

  // ── Sistema tipográfico único ──────────────────────────────────────────
  const tx = {
    heading: { fontSize: '0.85rem', fontWeight: 600,  color: '#1e293b' },
    label:   { fontSize: '0.72rem', fontWeight: 600,  color: '#64748b', letterSpacing: '0.1px' },
    body:    { fontSize: '0.72rem', fontWeight: 400,  color: '#475569' },
    muted:   { fontSize: '0.72rem', fontWeight: 400,  color: '#94a3b8', letterSpacing: '0.1px' },
    micro:   { fontSize: '0.62rem', fontWeight: 600,  color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' },
  };

  return (
    <div style={{ background: '#f9fafb', minHeight: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', paddingBottom: 16, marginBottom: 20, borderBottom: '1px solid #f1f5f9' }}>
        <div style={{ width: 38, height: 38, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)', fontSize: '1rem', marginRight: 14, flexShrink: 0 }}>
          <i className="bi bi-briefcase-fill" />
        </div>
        <div>
          <div style={{ ...tx.micro, marginBottom: 3 }}>Gestão de Pessoal · Controle de Ponto</div>
          <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#334155', letterSpacing: '-0.5px', lineHeight: 1.2 }}>Folha de Ponto</div>
        </div>
      </div>

      {/* ── Linha principal: upload + calendário + resultado ── */}
      <div className="row g-3 mb-3">

        {/* Card de upload */}
        <div className="col-lg-4">
          <div style={{
            background: '#ffffff',
            border: '1px solid #f1f5f9',
            borderRadius: 16,
            padding: 24,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
          }}>
            {/* Data */}
            <div>
              <label style={{ ...tx.micro, marginBottom: 6, display: 'block' }}>
                Data dos Registros
              </label>
              <div className="d-flex align-items-center gap-3">
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={data}
                  onChange={e => setData(e.target.value)}
                  style={{ maxWidth: 160, borderRadius: 8 }}
                />
                <span style={{ ...tx.muted }}>
                  <i className="bi bi-magic me-1" />
                  Preenchida pelo nome do arquivo
                </span>
              </div>
            </div>

            {/* Drop zone */}
            <div
              onClick={() => inputRef.current.click()}
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
              style={{
                border: `2px dashed ${dragging ? '#3B82F6' : arquivo ? '#059669' : '#e2e8f0'}`,
                borderRadius: 12,
                background: dragging ? '#eff6ff' : arquivo ? '#ecfdf5' : '#f8fafc',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '28px 16px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                height: 140,
              }}
            >
              <input ref={inputRef} type="file" accept=".xlsx,.xls" className="d-none"
                onChange={e => handleFile(e.target.files[0])} />

              {arquivo ? (
                <>
                  <div style={{ fontSize: '2rem', color: '#059669', marginBottom: 8 }}>
                    <i className="bi bi-file-earmark-excel-fill" />
                  </div>
                  <div style={{ ...tx.heading, color: '#065f46' }}>{arquivo.name}</div>
                  <div style={{ ...tx.muted, marginTop: 4 }}>
                    {(arquivo.size / 1024).toFixed(1)} KB · Clique para trocar
                  </div>
                </>
              ) : (
                <>
                  <div style={{ width: 56, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', color: dragging ? 'var(--primary)' : '#94a3b8', marginBottom: 8 }}>
                    <i className="bi bi-cloud-arrow-up" />
                  </div>
                  <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#334155' }}>
                    {dragging ? 'Solte o arquivo aqui' : 'Arraste a folha de ponto aqui'}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 4 }}>
                    ou clique para selecionar · <strong>.xlsx</strong> ou <strong>.xls</strong>
                  </div>
                  <div style={{ fontSize: '0.68rem', color: '#cbd5e1', marginTop: 8 }}>
                    Modelo: Matrícula · Nome · Data · Hora
                  </div>
                </>
              )}
            </div>

            {/* Erro */}
            {erro && (
              <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#991b1b', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                <i className="bi bi-exclamation-triangle-fill" /> {erro}
              </div>
            )}

            {/* Botão */}
            <button
              className="btn btn-primary-custom w-100"
              onClick={importar}
              disabled={loading || !arquivo}
              style={{ justifyContent: 'center', padding: '12px 0', fontSize: '0.85rem', borderRadius: 10, transition: 'all 0.15s ease' }}
            >
              {loading
                ? <><div className="spinner-border spinner-border-sm text-primary" style={{ marginRight: 8 }} />Processando...</>
                : <><i className="bi bi-upload me-2" />Importar Registros de Ponto</>}
            </button>
          </div>
        </div>

        {/* Calendário de importações */}
        <div className="col-lg-4">
          <div style={{ background: '#ffffff', border: '1px solid #f1f5f9', borderRadius: 16, padding: '18px 16px', height: '100%', display: 'flex', flexDirection: 'column', boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)' }}>

            {/* Cabeçalho */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <button onClick={() => setCalMes(p => { const d = new Date(p.ano, p.mes - 1); return { ano: d.getFullYear(), mes: d.getMonth() }; })}
                style={{ background: '#f1f5f9', border: 'none', cursor: 'pointer', color: '#64748b', width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem', transition: 'background 0.15s ease' }}>
                <i className="bi bi-chevron-left" />
              </button>
              <div style={{ textAlign: 'center' }}>
                <div style={{ ...tx.heading, fontSize: '0.85rem' }}>{MESES_CAL[calMes.mes]}</div>
                <div style={{ ...tx.muted }}>{calMes.ano}</div>
              </div>
              <button onClick={() => setCalMes(p => { const d = new Date(p.ano, p.mes + 1); return { ano: d.getFullYear(), mes: d.getMonth() }; })}
                style={{ background: '#f1f5f9', border: 'none', cursor: 'pointer', color: '#64748b', width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem', transition: 'background 0.15s ease' }}>
                <i className="bi bi-chevron-right" />
              </button>
            </div>

            {/* Dias da semana */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 6 }}>
              {['D','S','T','Q','Q','S','S'].map((d, i) => (
                <div key={i} style={{ textAlign: 'center', fontSize: '0.62rem', fontWeight: 700, color: '#94a3b8', padding: '2px 0' }}>{d}</div>
              ))}
            </div>

            {/* Células dos dias */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3, flex: 1 }}>
              {Array.from({ length: primeiroDia }).map((_, i) => <div key={`e${i}`} />)}
              {Array.from({ length: totalDias }).map((_, i) => {
                const dia = i + 1;
                const isoKey = `${calMes.ano}-${String(calMes.mes + 1).padStart(2,'0')}-${String(dia).padStart(2,'0')}`;
                const importado = diasImportados.has(isoKey);
                const ehHoje = isoKey === hoje;
                return (
                  <div key={dia}
                    title={importado ? `Ver cartão de ${String(dia).padStart(2,'0')}/${String(calMes.mes+1).padStart(2,'0')}/${calMes.ano}` : ''}
                    onClick={importado ? () => abrirDetalhe(`${String(dia).padStart(2,'0')}/${String(calMes.mes+1).padStart(2,'0')}/${calMes.ano}`) : undefined}
                    style={{
                      position: 'relative',
                      textAlign: 'center',
                      padding: '5px 0',
                      borderRadius: 8,
                      fontSize: '0.72rem',
                      fontWeight: importado || ehHoje ? 700 : 400,
                      cursor: importado ? 'pointer' : 'default',
                      transition: 'all 0.15s ease',
                      boxShadow: importado ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                      ...calDayStyle(isoKey, importado, ehHoje),
                    }}>
                    {importado && (
                      <div style={{ position: 'absolute', top: 2, right: 3, fontSize: '0.5rem', fontWeight: 700, background: 'rgba(0,0,0,0.15)', borderRadius: 3, padding: '1px 3px', lineHeight: 1 }}>
                        {comRegistroMap[isoKey] ?? 0}
                      </div>
                    )}
                    <div>{dia}</div>
                  </div>
                );
              })}
            </div>

            {/* Contador e legenda */}
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {[['#d1fae5','Baixo'],['#34d399','Médio'],['#059669','Alto'],['#065f46','Total']].map(([bg, label]) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <div style={{ width: 8, height: 8, background: bg, borderRadius: 2, flexShrink: 0 }} />
                    <span style={{ ...tx.micro, textTransform: 'none', letterSpacing: 0 }}>{label}</span>
                  </div>
                ))}
              </div>
              <div style={{ ...tx.label }}>
                {Array.from(diasImportados).filter(d => d.startsWith(`${calMes.ano}-${String(calMes.mes + 1).padStart(2,'0')}`)).length} dias
              </div>
            </div>
          </div>
        </div>

        {/* Card resultado — sempre visível (última importação ou atual) */}
        <div className="col-lg-4">
          <div style={{ background: '#ffffff', border: '1px solid #f1f5f9', borderRadius: 16, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)' }}>
            {!exibir ? (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1', gap: 10, padding: 24 }}>
                <i className="bi bi-bar-chart-steps" style={{ fontSize: '2.5rem' }} />
                <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>Nenhuma importação realizada</div>
                <div style={{ fontSize: '0.72rem' }}>O resultado aparecerá aqui após importar</div>
              </div>
            ) : (
              <>
                {/* Cabeçalho limpo */}
                <div style={{ padding: '16px 20px', borderBottom: `3px solid ${ok ? '#059669' : '#D97706'}`, display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 38, height: 38, background: ok ? '#ecfdf5' : '#fef3c7', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', color: ok ? '#059669' : '#D97706', flexShrink: 0 }}>
                    <i className={`bi ${ok ? 'bi-check-circle-fill' : 'bi-exclamation-circle-fill'}`} />
                  </div>
                  <div>
                    <div style={{ ...tx.micro }}>
                      {ok ? 'Importação concluída' : 'Concluída com erros'}
                    </div>
                    <div style={{ ...tx.muted, marginTop: 2 }}>
                      Ref.: {exibir.data}
                      {exibir._doHistorico && <span style={{ marginLeft: 6, background: '#f1f5f9', borderRadius: 4, padding: '1px 6px', ...tx.muted }}>última</span>}
                    </div>
                  </div>
                </div>

                {/* Stats — compact inline */}
                <div style={{ padding: '8px 16px', flex: 1 }}>
                  {[
                    { label: 'Linhas',      value: exibir.total,      color: '#2563EB', light: '#eff6ff', icon: 'bi-list-ol' },
                    { label: 'Importados',  value: exibir.importados, color: '#059669', light: '#ecfdf5', icon: 'bi-check2-all' },
                    { label: 'Erros',       value: exibir.erros,      color: exibir.erros > 0 ? '#DC2626' : '#94a3b8', light: exibir.erros > 0 ? '#fee2e2' : '#f8fafc', icon: 'bi-x-circle' },
                  ].map((s, idx, arr) => (
                    <div key={s.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: idx < arr.length - 1 ? '1px solid #f8fafc' : 'none' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 28, height: 28, background: s.light, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <i className={`bi ${s.icon}`} style={{ fontSize: '0.85rem', color: s.color }} />
                        </div>
                        <span style={{ ...tx.label, color: '#64748b' }}>{s.label}</span>
                      </div>
                      <span style={{ fontWeight: 600, fontSize: '1rem', color: '#1e293b', fontWeight: 500, letterSpacing: '-0.3px' }}>{s.value}</span>
                    </div>
                  ))}
                </div>

                {/* Mensagem */}
                <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 8, ...tx.body, color: ok ? '#059669' : '#92400e', background: ok ? '#ecfdf5' : '#fffbeb', borderTop: `1px solid ${ok ? '#6ee7b7' : '#fde68a'}` }}>
                  <i className={`bi ${ok ? 'bi-check-circle-fill' : 'bi-exclamation-circle-fill'} flex-shrink-0`} />
                  <span>{exibir.mensagem}</span>
                </div>

                {exibir.log_erros?.length > 0 && (
                  <div style={{ padding: '0 18px 14px' }}>
                    <div style={{ ...tx.micro, color: '#DC2626', marginBottom: 4 }}>
                      <i className="bi bi-bug me-1" />Log de erros
                    </div>
                    <div className="log-box">
                      {exibir.log_erros.map((e, i) => <div key={i}>▸ {e}</div>)}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Histórico + Gráfico lado a lado ── */}
      <div className="row g-3">

      {/* Histórico */}
      <div className="col-lg-8">
      <div style={{ background: '#ffffff', border: '1px solid #f1f5f9', borderRadius: 16, overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%', boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)' }}>
        {/* Cabeçalho */}
        <div style={{
          padding: '14px 20px',
          borderBottom: '1px solid #f1f5f9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: '#f8fafc',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 28, height: 28, background: '#E0E7FF', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4F46E5', fontSize: '0.85rem' }}>
              <i className="bi bi-clock-history" />
            </div>
            <span style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.8rem' }}>Últimas Importações</span>
            {historico.length > 0 && (
              <span style={{ background: '#E0E7FF', color: '#4F46E5', borderRadius: 20, padding: '1px 8px', ...tx.micro, color: '#4F46E5' }}>
                {historico.length}
              </span>
            )}
          </div>
          <div />
        </div>

        {historico.length === 0 ? (
          <div style={{ padding: '48px 0', textAlign: 'center', color: '#cbd5e1' }}>
            <i className="bi bi-inbox" style={{ fontSize: '2rem', display: 'block', marginBottom: 8 }} />
            <p style={{ fontSize: '0.85rem', margin: 0 }}>Nenhuma importação realizada ainda.</p>
          </div>
        ) : (() => {
          const totalPags = Math.ceil(historico.length / POR_PAG);
          const paginaAtual = Math.min(pagHist, totalPags);
          const inicio = (paginaAtual - 1) * POR_PAG;
          const pagina = historico.slice(inicio, inicio + POR_PAG);
          return (
            <>
              <div className="table-responsive">
                <table className="table-ponto" style={{ width: '100%', fontSize: '0.72rem' }}>
                  <colgroup>
                    <col style={{ width: 160 }} />
                    <col />
                    <col style={{ width: 100 }} />
                    <col style={{ width: 70 }} />
                    <col style={{ width: 80 }} />
                    <col style={{ width: 60 }} />
                    <col style={{ width: 90 }} />
                    <col style={{ width: 90 }} />
                    <col style={{ width: 100 }} />
                    <col style={{ width: 70 }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th style={{ paddingLeft: 20 }}>Data/Hora</th>
                      <th>Arquivo</th>
                      <th>Data Reg.</th>
                      <th style={{ textAlign: 'center' }}>Linhas</th>
                      <th style={{ textAlign: 'center' }}>Importados</th>
                      <th style={{ textAlign: 'center' }}>Erros</th>
                      <th style={{ textAlign: 'center' }}>Com Reg.</th>
                      <th style={{ textAlign: 'center' }}>Sem Reg.</th>
                      <th style={{ textAlign: 'center' }}>Status</th>
                      <th style={{ textAlign: 'center' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagina.map((h, i) => (
                      <tr key={inicio + i} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                        <td style={{ paddingLeft: 20, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                          <i className="bi bi-calendar3 me-1" />{h.em}
                        </td>
                        <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <i className="bi bi-file-earmark-excel me-1 text-success" />{h.arquivo}
                        </td>
                        <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{h.data}</td>
                        <td style={{ textAlign: 'center', fontWeight: 500, letterSpacing: '-0.3px' }}>{h.total}</td>
                        <td style={{ textAlign: 'center', fontWeight: 500, letterSpacing: '-0.3px', color: '#059669' }}>{h.importados}</td>
                        <td style={{ textAlign: 'center', fontWeight: 500, letterSpacing: '-0.3px', color: h.erros > 0 ? '#DC2626' : '#94a3b8' }}>{h.erros}</td>
                        <td style={{ textAlign: 'center' }}>
                          {h.com_registro != null
                            ? <span style={{ background: '#ecfdf5', color: '#059669', borderRadius: 20, padding: '1px 8px', fontWeight: 500, letterSpacing: '-0.3px' }}>{h.com_registro}</span>
                            : <span style={{ color: '#cbd5e1' }}>—</span>}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {h.sem_registro != null
                            ? <span style={{ background: h.sem_registro > 0 ? '#fee2e2' : '#f8fafc', color: h.sem_registro > 0 ? '#DC2626' : '#94a3b8', borderRadius: 20, padding: '1px 8px', fontWeight: 500, letterSpacing: '-0.3px' }}>{h.sem_registro}</span>
                            : <span style={{ color: '#cbd5e1' }}>—</span>}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span style={{ background: h.erros === 0 ? '#ecfdf5' : '#fef3c7', color: h.erros === 0 ? '#065f46' : '#92400e', borderRadius: 20, padding: '2px 10px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <i className={`bi ${h.erros === 0 ? 'bi-check-circle-fill' : 'bi-exclamation-circle-fill'}`} />
                            {h.erros === 0 ? 'OK' : 'Com erros'}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button onClick={() => abrirDetalhe(h.data)} title="Ver cartão de ponto"
                            style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: '2px 4px', borderRadius: 4, transition: 'color 0.15s', display: 'inline-flex', alignItems: 'center' }}>
                            <i className="bi bi-eye" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalPags > 1 && (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px 20px', borderTop: '1px solid #f1f5f9', background: '#f8fafc',
                }}>
                  <span style={{ ...tx.muted }}>
                    Exibindo {inicio + 1}–{Math.min(inicio + POR_PAG, historico.length)} de {historico.length} registros
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <button
                      onClick={() => setPagHist(p => Math.max(1, p - 1))}
                      disabled={paginaAtual === 1}
                      style={{ border: '1px solid #e2e8f0', background: '#fff', borderRadius: 6, padding: '4px 10px', fontSize: '0.72rem', color: '#64748b', cursor: paginaAtual === 1 ? 'default' : 'pointer', opacity: paginaAtual === 1 ? 0.4 : 1, transition: 'all 0.15s ease' }}
                    >
                      <i className="bi bi-chevron-left" />
                    </button>
                    {Array.from({ length: totalPags }, (_, i) => i + 1).map(p => (
                      <button
                        key={p}
                        onClick={() => setPagHist(p)}
                        style={{
                          border: `1px solid ${p === paginaAtual ? 'var(--primary)' : '#e2e8f0'}`,
                          background: p === paginaAtual ? 'var(--primary)' : '#fff',
                          color: p === paginaAtual ? '#fff' : '#64748b',
                          borderRadius: 6, padding: '4px 10px', fontSize: '0.72rem',
                          fontWeight: p === paginaAtual ? 700 : 400, cursor: 'pointer',
                          minWidth: 32,
                          transition: 'all 0.15s ease',
                        }}
                      >
                        {p}
                      </button>
                    ))}
                    <button
                      onClick={() => setPagHist(p => Math.min(totalPags, p + 1))}
                      disabled={paginaAtual === totalPags}
                      style={{ border: '1px solid #e2e8f0', background: '#fff', borderRadius: 6, padding: '4px 10px', fontSize: '0.72rem', color: '#64748b', cursor: paginaAtual === totalPags ? 'default' : 'pointer', opacity: paginaAtual === totalPags ? 0.4 : 1, transition: 'all 0.15s ease' }}
                    >
                      <i className="bi bi-chevron-right" />
                    </button>
                  </div>
                </div>
              )}
            </>
          );
        })()}
      </div>{/* fim inner historico */}
      </div>{/* fim col-lg-8 */}

      {/* ── Gráfico ── */}
      <div className="col-lg-4">
        <div style={{ background: '#ffffff', border: '1px solid #f1f5f9', borderRadius: 16, padding: '18px 20px', display: 'flex', flexDirection: 'column', height: '100%', boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <div style={{ width: 28, height: 28, background: '#dbeafe', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2563EB', fontSize: '0.85rem' }}>
              <i className="bi bi-bar-chart-fill" />
            </div>
            <span style={{ ...tx.micro }}>
              {MESES_CAL[calMes.mes]} {calMes.ano}
            </span>
          </div>
          {dadosGrafico.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={dadosGrafico} barCategoryGap="30%" barGap={3} margin={{ top: 18, right: 8, left: -24, bottom: 0 }} style={{ cursor: 'pointer' }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="dia" tick={{ fontSize: '0.62rem', fill: '#64748b' }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: '0.62rem', fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <Bar dataKey="com" stackId="a" fill="#34a87a" radius={[0, 0, 0, 0]} maxBarSize={52}
                  onClick={(data) => abrirDetalhe(`${String(data.dia).padStart(2,'0')}/${String(calMes.mes+1).padStart(2,'0')}/${calMes.ano}`)} />
                <Bar dataKey="sem" stackId="a" fill="#d44444" radius={[4, 4, 0, 0]} maxBarSize={52}
                  onClick={(data) => abrirDetalhe(`${String(data.dia).padStart(2,'0')}/${String(calMes.mes+1).padStart(2,'0')}/${calMes.ano}`)}>
                  <LabelList dataKey={(d) => d.com + d.sem} position="top" style={{ fontSize: '0.62rem', fill: '#374151', fontWeight: 700 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1', fontSize: '0.85rem' }}>
              Nenhum dado disponível
            </div>
          )}
        </div>
      </div>
      </div>{/* fim row g-3 histórico+gráfico */}

      {/* ── Modal de detalhe do cartão de ponto ── */}
      {detalheModal && (() => {
        const todosRegs = detalheModal.registros;
        const setores = [...new Set(todosRegs.map(r => r.funcionario_departamento_nome).filter(Boolean))].sort();
        const regs = setorFiltro ? todosRegs.filter(r => r.funcionario_departamento_nome === setorFiltro) : todosRegs;
        return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1060, background: 'rgba(10,16,30,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(3px)', padding: '32px' }}
          onClick={(e) => { if (e.target === e.currentTarget) setDetalheModal(null); }}>
          <div style={{ background: '#fff', borderRadius: 20, boxShadow: '0 32px 80px rgba(0,0,0,0.28)', width: '100%', maxWidth: 1240, maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

            {/* Header clean */}
            <div style={{ padding: '14px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ width: 38, height: 38, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)', fontSize: '1rem', flexShrink: 0 }}>
                  <i className="bi bi-briefcase-fill" />
                </div>
                <div>
                  <div style={{ ...tx.micro, marginBottom: 2 }}>Gestão de Pessoal · Controle de Ponto</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: '1rem', fontWeight: 600, color: '#334155' }}>Cartão de Ponto</span>
                    <span style={{ ...tx.muted, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <i className="bi bi-calendar3" style={{ fontSize: '0.68rem' }} />{detalheModal.data}
                    </span>
                    <span style={{ ...tx.micro, background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 20, padding: '1px 8px', color: '#64748b' }}>
                      somente consulta
                    </span>
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {setores.length > 0 && (
                  <select
                    value={setorFiltro}
                    onChange={e => setSetorFiltro(e.target.value)}
                    style={{ fontSize: '0.75rem', borderRadius: 8, border: '1px solid #e2e8f0', padding: '5px 10px', color: '#334155', background: '#f8fafc', cursor: 'pointer', maxWidth: 200 }}>
                    <option value="">Todos os setores</option>
                    {setores.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                )}
                <button onClick={() => setDetalheModal(null)}
                  style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '7px 10px', cursor: 'pointer', color: '#94a3b8', fontSize: '0.85rem', display: 'flex', alignItems: 'center', transition: 'all 0.15s ease' }}>
                  <i className="bi bi-x-lg" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div style={{ overflowY: 'auto', overflowX: 'auto', height: '72vh', minHeight: 400 }}>
              {detalheModal.loading ? (
                <div style={{ padding: '56px 0', textAlign: 'center', color: '#94a3b8' }}>
                  <span className="spinner-border spinner-border-sm me-2" />Carregando registros...
                </div>
              ) : detalheModal.erro ? (
                <div style={{ padding: 24, color: '#DC2626', fontSize: '0.85rem' }}><i className="bi bi-exclamation-triangle me-2" />{detalheModal.erro}</div>
              ) : regs.length === 0 ? (
                <div style={{ padding: '56px 0', textAlign: 'center', color: '#94a3b8' }}>
                  <i className="bi bi-inbox" style={{ fontSize: '2.5rem', display: 'block', marginBottom: 10 }} />
                  <p style={{ margin: 0, fontSize: '0.85rem' }}>Nenhum registro encontrado para esta data.</p>
                </div>
              ) : (
                <div>
                  <table style={{ width: '100%', fontSize: '0.72rem', tableLayout: 'fixed', borderCollapse: 'collapse' }}>
                    <colgroup>
                      <col style={{ width: 60 }} />
                      <col style={{ width: 280 }} />
                      {[...Array(8)].map((_, i) => <col key={i} style={{ width: 62 }} />)}
                      <col style={{ width: 68 }} />
                      <col style={{ width: 68 }} />
                      <col style={{ width: 120 }} />
                      <col style={{ width: 62 }} />
                      <col style={{ width: 62 }} />
                    </colgroup>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 4 }}>
                      <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                        <th style={{ position: 'sticky', left: 0, zIndex: 5, background: '#f8fafc', paddingLeft: 40, paddingRight: 4, paddingTop: 7, paddingBottom: 7, textAlign: 'left', ...tx.micro, color: '#cbd5e1' }}>#</th>
                        <th style={{ position: 'sticky', left: 60, zIndex: 5, background: '#f8fafc', paddingLeft: 10, paddingTop: 7, paddingBottom: 7, textAlign: 'left', ...tx.micro, color: '#94a3b8', borderRight: '1px solid #f1f5f9' }}>Funcionário</th>
                        {[['Ent. 1','#059669'],['Saí. 1','#DC2626'],['Ent. 2','#059669'],['Saí. 2','#DC2626'],['Ent. 3','#059669'],['Saí. 3','#DC2626'],['Ent. 4','#059669'],['Saí. 4','#DC2626']].map(([label, color]) => (
                          <th key={label} style={{ textAlign: 'center', ...tx.micro, color, paddingTop: 7, paddingBottom: 7 }}>{label}</th>
                        ))}
                        <th style={{ textAlign: 'center', ...tx.micro, color: '#2563EB', paddingTop: 7, paddingBottom: 7 }}>Trab.</th>
                        <th style={{ textAlign: 'center', ...tx.micro, color: '#7C3AED', paddingTop: 7, paddingBottom: 7 }}>Extra</th>
                        <th style={{ padding: '7px 16px', ...tx.micro, color: '#94a3b8' }}>Obs.</th>
                        <th style={{ textAlign: 'center', ...tx.micro, color: '#0891b2', paddingTop: 7, paddingBottom: 7 }}>Per.</th>
                        <th style={{ textAlign: 'center', ...tx.micro, color: '#a855f7', paddingTop: 7, paddingBottom: 7 }}>Alm.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        // verifica se o cartão é do dia atual
                        const [dd, mm, yyyy] = (detalheModal.data || '').split('/');
                        const ehHoje = `${yyyy}-${mm}-${dd}` === hojeISO();

                        const toMin = t => { if (!t) return null; const [h, m] = t.slice(0,5).split(':').map(Number); return h * 60 + m; };
                        const fromMin = m => `${String(Math.floor(m / 60)).padStart(2,'0')}:${String(m % 60).padStart(2,'0')}`;

                        const projetarSaida = (r) => {
                          if (!ehHoje) return null;
                          if (!r.hora_entrada || r.hora_saida) return null; // já bateu ou sem entrada
                          if (!r.horas_previstas) return null;
                          const prevMin = Math.round(parseFloat(r.horas_previstas) * 60);
                          const entMin  = toMin(r.hora_entrada);
                          if (r.hora_retorno_almoco) {
                            // já voltou do almoço — calcula o restante
                            if (!r.hora_saida_almoco) return null; // SAÍ.1 ausente — sem projeção confiável
                            const manhaMin = toMin(r.hora_saida_almoco) - entMin;
                            const restante = prevMin - manhaMin;
                            return fromMin(toMin(r.hora_retorno_almoco) + restante);
                          }
                          if (r.hora_saida_almoco) {
                            // saiu pro almoço mas não voltou — sem projeção confiável
                            return null;
                          }
                          // ainda não foi almoçar — soma horas previstas + 1h de almoço (padrão)
                          return fromMin(entMin + prevMin + 60);
                        };

                        return regs.map((r, i) => {
                        const temHorario = !!r.hora_entrada;
                        const bg = i % 2 === 0 ? '#fff' : '#f8fafc';
                        const projecao = projetarSaida(r);

                        // almoço tardio dinâmico para hoje sem saida_almoco ainda
                        const almocoTardioHoje = ehHoje && r.hora_entrada && !r.hora_saida_almoco &&
                          (() => { const m = toMin(r.hora_entrada); return m !== null && (Date.now() - new Date().setHours(...r.hora_entrada.slice(0,5).split(':').map(Number), 0, 0)) > 5 * 3600000; })();
                        const almocoTardio    = r.almoco_tardio    || almocoTardioHoje;
                        const almocoPrecoce   = r.almoco_precoce   && !almocoTardio;
                        const almocoCurto     = r.almoco_curto     || false;
                        const entradaAtrasada = r.entrada_atrasada || false;

                        // saída após 18h15 — dinâmico para hoje (projeção ou real)
                        const toMin1815 = 18 * 60 + 15;
                        // índice da coluna SAÍ.2 = 3 (hora_saida)
                        const horarios = [r.hora_entrada, r.hora_saida_almoco, r.hora_retorno_almoco, r.hora_saida, r.hora_entrada3, r.hora_saida3, r.hora_entrada4, r.hora_saida4];
                        const saidaApos1815DB = r.saida_apos_1815;
                        // índice da última saída real preenchida (3=hora_saida, 5=saida3, 7=saida4)
                        const ultimaSaidaIdx = [7, 5, 3].find(idx => horarios[idx]);
                        const saidaApos1815 = saidaApos1815DB ||
                          (ehHoje && projecao && toMin(projecao) > toMin1815);
                        return (
                          <tr key={r.id} style={{ background: bg, borderBottom: '1px solid #f1f5f9' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
                            onMouseLeave={e => e.currentTarget.style.background = bg}>
                            <td style={{ position: 'sticky', left: 0, zIndex: 1, background: 'inherit', textAlign: 'center', ...tx.muted, fontVariantNumeric: 'tabular-nums' }}>
                              {i + 1}
                            </td>
                            <td style={{ position: 'sticky', left: 60, zIndex: 1, background: 'inherit', borderRight: '1px solid #f1f5f9' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                                <i className={temHorario ? 'bi bi-check-circle-fill' : 'bi bi-dash-circle'} style={{ fontSize: '0.72rem', color: temHorario ? '#059669' : '#cbd5e1', flexShrink: 0 }} />
                                <span style={{ ...tx.body, fontWeight: 600, color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 230 }}>
                                  {r.funcionario_nome || r.funcionario?.nome || '—'}
                                </span>
                              </div>
                            </td>
                            {horarios.map((h, idx) => {
                              const destacarEntrada = entradaAtrasada && idx === 0;
                              const destacarAlmoco  = almocoTardio   && idx === 1;
                              const destacarPrecoce = almocoPrecoce  && idx === 1;
                              const destacarCurto   = almocoCurto    && idx === 2;
                              const destacarSaida   = saidaApos1815  && idx === ultimaSaidaIdx;
                              const bgCell = destacarEntrada ? 'rgba(234,179,8,0.12)'
                                           : destacarAlmoco  ? 'rgba(251,146,60,0.12)'
                                           : destacarPrecoce ? 'rgba(59,130,246,0.10)'
                                           : destacarCurto   ? 'rgba(168,85,247,0.10)'
                                           : destacarSaida   ? 'rgba(239,68,68,0.10)'
                                           : 'transparent';
                              return (
                                <td key={idx} style={{ textAlign: 'center', fontVariantNumeric: 'tabular-nums', background: bgCell }}>
                                  {h ? (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                                      {idx === 0 && entradaAtrasada && (
                                        <i className="bi bi-alarm-fill" title="Entrada atrasada: mais de 15min após o horário previsto" style={{ color: '#ca8a04', fontSize: '0.7rem' }} />
                                      )}
                                      {idx === 1 && almocoTardio && (
                                        <i className="bi bi-exclamation-triangle-fill" title="Almoço tardio: mais de 5h após a entrada" style={{ color: '#f97316', fontSize: '0.7rem' }} />
                                      )}
                                      {idx === 1 && almocoPrecoce && !almocoTardio && (
                                        <i className="bi bi-clock-fill" title="Almoço precoce: menos de 4h após a entrada" style={{ color: '#3b82f6', fontSize: '0.7rem' }} />
                                      )}
                                      {idx === 2 && almocoCurto && (
                                        <i className="bi bi-cup-hot-fill" title="Almoço curto: menos de 1h de intervalo" style={{ color: '#a855f7', fontSize: '0.7rem' }} />
                                      )}
                                      {destacarSaida && (
                                        <i className="bi bi-moon-stars-fill" title="Saída após 18h15" style={{ color: '#dc2626', fontSize: '0.7rem' }} />
                                      )}
                                      <span style={{ ...tx.body, color: idx % 2 === 0 ? '#059669' : '#DC2626', fontWeight: 600 }}>{h.slice(0,5)}</span>
                                    </div>
                                  ) : idx === 3 && projecao ? (
                                    <span style={{ color: saidaApos1815 ? '#dc2626' : '#94a3b8', fontWeight: 500, fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums', display: 'inline-flex', alignItems: 'center', gap: 3 }}
                                          title={saidaApos1815 ? 'Projeção após 18h15' : 'Projeção de saída'}>
                                      {saidaApos1815 && <i className="bi bi-moon-stars-fill" style={{ fontSize: '0.6rem' }} />}
                                      {projecao}
                                    </span>
                                  ) : (
                                    <span style={{ color: '#e2e8f0' }}>—</span>
                                  )}
                                </td>
                              );
                            })}
                            <td style={{ textAlign: 'center', ...tx.body, fontWeight: 500, letterSpacing: '-0.3px', color: '#2563EB', fontVariantNumeric: 'tabular-nums' }}>
                              {decParaHHMM(r.horas_trabalhadas)}
                            </td>
                            <td style={{ textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                              {parseFloat(r.horas_extras) > 0
                                ? <span style={{ ...tx.body, fontWeight: 500, letterSpacing: '-0.3px', color: '#7C3AED' }}>{decParaHHMM(r.horas_extras)}</span>
                                : <span style={{ color: '#e2e8f0' }}>—</span>}
                            </td>
                            <td style={{ paddingLeft: 16, paddingRight: 16, ...tx.body, color: r.observacao ? '#475569' : '#e2e8f0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 }}>
                              {r.observacao || '—'}
                            </td>
                            <td style={{ textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                              {(() => {
                                const e1 = toMin(r.hora_entrada);
                                const s1 = toMin(r.hora_saida_almoco);
                                if (e1 === null || s1 === null) return <span style={{ color: '#e2e8f0' }}>—</span>;
                                const diff = s1 - e1;
                                if (diff <= 0) return <span style={{ color: '#e2e8f0' }}>—</span>;
                                const dur = fromMin(diff);
                                const curto = diff < 240;  // < 4h
                                const longo = diff > 300;  // > 5h
                                return (
                                  <span style={{ ...tx.body, fontWeight: 500, color: curto ? '#3b82f6' : longo ? '#f97316' : '#64748b', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                    {curto && <i className="bi bi-arrow-down-circle-fill" title="Período curto: menos de 4h entre Ent.1 e Saí.2" style={{ fontSize: '0.6rem' }} />}
                                    {longo && <i className="bi bi-arrow-up-circle-fill"   title="Período longo: mais de 5h entre Ent.1 e Saí.2"  style={{ fontSize: '0.6rem' }} />}
                                    {dur}
                                  </span>
                                );
                              })()}
                            </td>
                            <td style={{ textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                              {(() => {
                                if (!r.hora_saida_almoco || !r.hora_retorno_almoco) return <span style={{ color: '#e2e8f0' }}>—</span>;
                                const diff = toMin(r.hora_retorno_almoco) - toMin(r.hora_saida_almoco);
                                if (diff <= 0) return <span style={{ color: '#e2e8f0' }}>—</span>;
                                const dur = fromMin(diff);
                                const longo = diff > 65;
                                const curto = diff < 60;
                                return (
                                  <span style={{ ...tx.body, fontWeight: 500, color: curto ? '#a855f7' : longo ? '#f97316' : '#64748b', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                    {curto  && <i className="bi bi-cup-hot-fill"      title="Almoço curto: menos de 1h"  style={{ fontSize: '0.6rem' }} />}
                                    {longo  && <i className="bi bi-hourglass-split"   title="Almoço longo: mais de 1h"   style={{ fontSize: '0.6rem' }} />}
                                    {dur}
                                  </span>
                                );
                              })()}
                            </td>
                          </tr>
                        );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
              )}
            </div>


          </div>
        </div>
        );
      })()}

      {/* ── Modal de confirmação de sobrescrita ── */}
      {confirmModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1050,
          background: 'rgba(15,23,42,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(2px)',
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 16,
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            width: '100%',
            maxWidth: 440,
            overflow: 'hidden',
            animation: 'fadeIn 0.15s ease',
          }}>
            {/* Header */}
            <div style={{ background: '#fffbeb', borderBottom: '1px solid #fde68a', padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ width: 44, height: 44, background: '#fef3c7', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: '1.25rem', color: '#D97706' }} />
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: '1rem', color: '#1e293b' }}>Data já importada</div>
                <div style={{ fontSize: '0.72rem', color: '#92400e', marginTop: 2, letterSpacing: '0.1px' }}>
                  Existe uma importação anterior para esta data
                </div>
              </div>
            </div>

            {/* Body */}
            <div style={{ padding: '24px' }}>
              <div style={{ background: '#f8fafc', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: '0.62rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Data dos registros</span>
                  <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>{confirmModal.dataFormatada}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.62rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Importada em</span>
                  <span style={{ fontSize: '0.85rem', color: '#64748b' }}>{confirmModal.em}</span>
                </div>
              </div>
              <p style={{ fontSize: '0.85rem', color: '#475569', margin: 0 }}>
                Ao confirmar, os registros existentes desta data serão <strong style={{ color: '#DC2626' }}>sobrescritos</strong> pelos dados do novo arquivo. Esta ação não pode ser desfeita.
              </p>
            </div>

            {/* Footer */}
            <div style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setConfirmModal(null)}
                style={{ background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', transition: 'all 0.15s ease' }}
              >
                Cancelar
              </button>
              <button
                onClick={() => { setConfirmModal(null); executarImportacao(); }}
                style={{ background: '#DC2626', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, transition: 'all 0.15s ease' }}
              >
                <i className="bi bi-arrow-repeat" />Sobrescrever
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
